import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/system';
import { PatientCaseWithTimetoken } from 'apps/wrapper/src/hooks/use-leads-management/use-leads-management';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { CallHistoryItem } from '../../../../hooks/use-communications/types/comms-call-types';
import { ManagedCommItem } from '../../../../hooks/use-communications/types/managed-communication';
import { useCommunications } from '../../../../hooks/use-communications/use-communications';
import { usePubnub } from '../../../../hooks/use-pubnub/use-pubnub';
import { Message } from '../../../../services/pubnub-service';
import { formatToLocale } from '../../../../utils/date-time';
import InboxSearchEmpty from '../inbox-widget/inbox-search-empty';
import { CommunicationCardFactory } from './communcation-card-factory';
import { CommsOverlineText, CommsSectionSeparator } from './styles/communcations-styles';
import { isAiCase } from './utils/is-ai-case';
import { isCallHistory } from './utils/is-call-history';
import { isManagedComm } from './utils/is-managed-comm';
import { isMessage } from './utils/is-message';

const ChatSkeleton = styled(Skeleton)({
  backgroundColor: '#f5f5f5'
});

interface CommunicationsPanelProps {
  timestamp?: number;
}

export default function CommunicationsPanel({ timestamp }: CommunicationsPanelProps) {
  const { data, isLoading, hasMore, loadMore } = useCommunications();

  const { mailboxes } = usePubnub();
  const [lastTimetoken, setLastTimetoken] = useState<number | undefined>();
  const [scrollHeight, setScrollHeight] = useState<number | undefined>();
  const [scrollToIndex, setScrollToIndex] = useState(-1);
  const scrollbarRef: any = useRef<Scrollbars>(null);
  const dates = new Set<string>();

  const loadNextPage = useCallback(() => {
    if (!isLoading && hasMore) {
      loadMore();
    }
  }, [loadMore, isLoading, hasMore]);

  const onScrollTop = useCallback(() => {
    const scroll = scrollbarRef.current;
    const values = scroll.getValues();

    if (values.scrollTop === 0) {
      setScrollHeight(values.scrollHeight);
      loadNextPage();
    }
  }, []);

  const loading = () => {
    return (
      <Box display="flex" alignItems="flex-end" flexDirection="column" padding={2}>
        <ChatSkeleton animation="wave" width={'50%'} height={35} />
        <ChatSkeleton animation="wave" width={'50%'} height={10} />
      </Box>
    );
  };

  useEffect(() => {
    const curTimetoken = data.length && data[data.length - 1].timetoken;
    const scroll = scrollbarRef.current;
    const values = scroll.getValues();
    if (lastTimetoken !== curTimetoken) {
      setLastTimetoken(curTimetoken);
      scroll.scrollToBottom();
      setScrollHeight(values.scrollHeight);
    } else if (scrollHeight && values.scrollHeight - scrollHeight > 25) {
      scroll.scrollTop(values.scrollHeight - scrollHeight);
      setScrollHeight(values.scrollHeight);
    }
  }, [data]);

  const renderCommunications = useCallback(
    (item: Message | CallHistoryItem | ManagedCommItem | PatientCaseWithTimetoken) => (
      <CommunicationCardFactory item={item} />
    ),
    [mailboxes]
  );

  const renderDateDevider = useCallback(
    (timetoken: string | null) => {
      if (timetoken && !dates.has(timetoken)) {
        dates.add(timetoken);
        return (
          <CommsSectionSeparator className="section-separator">
            <CommsOverlineText variant="overline">{formatToLocale(timetoken)}</CommsOverlineText>
          </CommsSectionSeparator>
        );
      }
      return null;
    },
    [dates]
  );

  useEffect(() => {
    if (!isLoading && scrollToIndex !== -1) {
      const item = scrollbarRef.current.view.querySelectorAll('.data-item')[scrollToIndex];
      if (item) {
        item.scrollIntoView({ block: 'center' });
        setScrollToIndex(-1);
      }
    }
  }, [scrollbarRef, isLoading, scrollToIndex, data]);

  useEffect(() => {
    if (timestamp && !isLoading && data.length) {
      const transformedDate = +new Date(isNaN(Number(timestamp)) ? timestamp : +timestamp);
      const firstIndexIsLessThanDate = data[0] && +new Date(data[0].timetoken || '') < transformedDate;

      // sometimes the transformed date can be slightly further in the future than the most recent message causing an infinite scroll
      if (data && data[data.length - 1] && data[data.length - 1].timetoken! < transformedDate) {
        return;
      }

      const findMessageByDate =
        !firstIndexIsLessThanDate && hasMore
          ? -1
          : data.findIndex((data) => {
              const messageDate = +new Date(data.timetoken || '');
              return messageDate >= timestamp;
            });

      if (findMessageByDate !== -1) {
        setScrollToIndex(findMessageByDate - 1);
      } else if (hasMore) {
        scrollbarRef.current.scrollTop(0);
        loadNextPage();
      }
    }
  }, [data, isLoading, scrollbarRef]);

  const renderedData = useMemo(() => {
    if (isLoading) return loading();
    if (data.length === 0) return <InboxSearchEmpty />;

    return data.map((item, index) => {
      const timetoken = isMessage(item)
        ? new Date(item.date).toLocaleDateString()
        : isCallHistory(item)
        ? new Date(item.callStarted).toLocaleDateString()
        : isManagedComm(item)
        ? new Date(item.date).toLocaleDateString()
        : isAiCase(item)
        ? new Date(item.timeOfCompletion!).toLocaleDateString()
        : null;

      return (
        <React.Fragment key={index}>
          {renderDateDevider(timetoken)}
          <Box
            className="data-item"
            alignItems="center"
            justifyContent="center"
            display="flex"
            marginY="24px"
            marginX="16px"
          >
            {renderCommunications(item)}
          </Box>
        </React.Fragment>
      );
    });
  }, [isLoading, data]);

  return (
    <Scrollbars autoHide ref={scrollbarRef} onScroll={onScrollTop}>
      {renderedData}
    </Scrollbars>
  );
}
